import {
  CollapsibleGraphGroup,
  getGraphNodeOrGroupLabel,
  GraphItemsModel,
  GraphNode,
  ItemLabels,
  RelationshipDiagramViewModelStreamState,
  TraversalAndGroupingResult,
} from '@ardoq/graph';
import { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import { toIdSet } from '@ardoq/core';
import type { I18nLocale } from '@ardoq/i18n';

type MutateItemLabelsArgs = {
  itemId: string;
  items: GraphItemsModel<GraphNode | CollapsibleGraphGroup>;
  nodeLabelsData: Map<string, ItemLabels>;
  rawGraph: TraversalAndGroupingResult;
  isGroup?: boolean;
  isViewpointMode: boolean;
  i18nLocale: I18nLocale;
  componentsById: Map<ArdoqId, APIComponentAttributes>;
};
const mutateItemLabels = ({
  itemId,
  items,
  nodeLabelsData,
  rawGraph,
  isGroup,
  isViewpointMode,
  i18nLocale,
  componentsById,
}: MutateItemLabelsArgs) => {
  const graphItem = items.byId.get(itemId);

  if (!graphItem) {
    return;
  }

  /**
   * Group map is indexed by id paths, while component map is indexed by model id.
   */
  const rawGraphItemsMapIndex = isGroup ? graphItem.id : graphItem.modelId;
  const rawItemsMap = isGroup ? rawGraph.groupMap : rawGraph.componentMap;

  const rawGraphItem = rawItemsMap.get(rawGraphItemsMapIndex);

  if (!rawGraphItem) {
    return;
  }

  const freshName =
    !isGroup && graphItem.modelId
      ? componentsById.get(graphItem.modelId)?.name
      : undefined;
  const itemWithFreshName = freshName
    ? { ...rawGraphItem, name: freshName }
    : rawGraphItem;

  const newLabels = getGraphNodeOrGroupLabel(
    itemWithFreshName,
    isViewpointMode,
    i18nLocale
  );

  graphItem.setItemLabels(newLabels);
  nodeLabelsData.set(graphItem.id, newLabels);
};

type UpdateItemsArgs<T extends GraphNode | CollapsibleGraphGroup> = {
  items: GraphItemsModel<T>;
  updatedModelIds: Set<string>;
  nodeLabelsData: Map<string, ItemLabels>;
  rawGraph: TraversalAndGroupingResult;
  isViewpointMode: boolean;
  isGroup?: boolean;
  i18nLocale: I18nLocale;
  componentsById: Map<ArdoqId, APIComponentAttributes>;
};
/**
 * Identifies which graph items in the view model correspond to components that have been updated
 * and moves these items from the 'add' array to the 'update', to trigger appropriate animations.
 *
 * To ensure label updates on field change, mutateItemLabels calls the GraphItem.setLabel() with
 * the label data, freshly composed from the raw graph item.
 * This could have its own reducer but it doesn't.
 *
 * @param items - The graph items model containing nodes or groups (byId map, add/update/remove arrays)
 * @param updatedModelIds - Set of component model IDs that have been updated
 * @param nodeLabelsData - Map storing the label data for each item by item ID
 * @param rawGraph - The raw graph data containing the source component and group information
 * @param isViewpointMode - Whether the view is in viewpoint mode (affects label generation)
 * @param isGroup - Whether the items being updated are groups (true) or nodes (false/undefined)
 * @returns Updated GraphItemsModel with refreshed labels and items moved to the update array
 */
export const updateItems = <T extends GraphNode | CollapsibleGraphGroup>({
  items,
  updatedModelIds,
  nodeLabelsData,
  rawGraph,
  isViewpointMode,
  isGroup,
  i18nLocale,
  componentsById,
}: UpdateItemsArgs<T>) => {
  const updatedItemIds = new Set(
    [...items.add, ...items.update].filter(itemId => {
      const item = items.byId.get(itemId);
      return item && updatedModelIds.has(item.modelId);
    })
  );

  updatedItemIds.forEach(itemId =>
    mutateItemLabels({
      itemId,
      items,
      nodeLabelsData,
      rawGraph,
      isGroup,
      isViewpointMode,
      i18nLocale,
      componentsById,
    })
  );

  return {
    ...items,
    add: items.add.filter(itemId => !updatedItemIds.has(itemId)),
    update: [...new Set([...items.update, ...updatedItemIds])],
  };
};

export const handleComponentsUpdate = <
  TState extends RelationshipDiagramViewModelStreamState,
>(
  state: TState,
  components: APIComponentAttributes[]
): TState => {
  const {
    viewModel,
    streamState: { rawGraph },
  } = state;
  const { nodeLabelsData } = viewModel;
  const { nodes, groups } = viewModel;
  const updatedModelIds = toIdSet(components);
  const componentsById = new Map(components.map(c => [c._id, c]));
  return {
    ...state,
    streamState: {
      ...state.streamState,
      skipRender: false,
    },
    viewModel: {
      ...viewModel,
      nodes: updateItems({
        items: nodes,
        updatedModelIds,
        nodeLabelsData,
        rawGraph,
        isViewpointMode: state.loadedGraph.isViewpointMode,
        i18nLocale: state.i18nLocale,
        componentsById,
      }),
      groups: updateItems({
        items: groups,
        updatedModelIds,
        nodeLabelsData,
        rawGraph,
        isGroup: true,
        isViewpointMode: state.loadedGraph.isViewpointMode,
        i18nLocale: state.i18nLocale,
        componentsById,
      }),
      expandedStateChangingGroupId: null,
    },
  };
};
